import type {
  B2BRequest,
  B2BRequestStatus,
  CreateCheckinRequest,
  CreateCheckinResponse,
  Enterprise,
  Excursion,
  PartnerSlotInput,
  Site,
  Slot,
  SlotStatus,
  VerifyPassResponse,
} from '@promtver/contracts';

import { apiRequest } from './client';
import type {
  B2BRequestPage,
  BlackoutDate,
  CoordinatorContact,
  ExcursionInput,
  GatePassInfo,
  OfflineVerificationKey,
  PartnerBookingsView,
  PartnerReportView,
  PartnerSlotView,
  WeeklySchedule,
} from './types';

/**
 * Обращения к backend API для роли object_moderator (ТЗ §7.1, Приложение В).
 * Права проверяются на сервере: модератор видит только свой объект.
 */

/* ─────────────────────────  ОБЪЕКТ ПРЕДПРИЯТИЯ  ───────────────────────── */

export async function getPartnerEnterprise(): Promise<Enterprise> {
  const body = await apiRequest<{ enterprise: Enterprise } | Enterprise>('/partner/enterprise');
  if (body && 'enterprise' in body) {
    return body.enterprise;
  }
  return body as Enterprise;
}

/* ────────────────────────────  СЛОТЫ  ────────────────────────────── */

export async function getPartnerSlots(date: string): Promise<PartnerSlotView[]> {
  const body = await apiRequest<{ slots?: PartnerSlotView[] } | PartnerSlotView[]>('/partner/slots', {
    query: { date },
  });
  if (Array.isArray(body)) return body;
  return body.slots ?? [];
}

export async function createPartnerSlot(input: PartnerSlotInput): Promise<Slot> {
  const body = await apiRequest<{ slot: Slot } | Slot>('/partner/slots', {
    method: 'POST',
    body: input,
  });
  if (body && 'slot' in body) return body.slot;
  return body as Slot;
}

export async function updatePartnerSlot(
  id: string,
  patch: { status?: SlotStatus; capacity?: number; endsAt?: string },
): Promise<Slot> {
  const body = await apiRequest<{ slot: Slot } | Slot>(`/partner/slots/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: patch,
  });
  if (body && 'slot' in body) return body.slot;
  return body as Slot;
}

/* ─────────────────────  РАСПИСАНИЕ И ИСКЛЮЧЕНИЯ  ───────────────────── */

export async function getWeeklySchedule(): Promise<WeeklySchedule> {
  const body = await apiRequest<{ schedule: WeeklySchedule } | WeeklySchedule>(
    '/partner/schedule/weekly',
  );
  if (body && 'schedule' in body) return body.schedule;
  return body as WeeklySchedule;
}

export async function saveWeeklySchedule(schedule: WeeklySchedule): Promise<WeeklySchedule> {
  const body = await apiRequest<{ schedule: WeeklySchedule } | WeeklySchedule>(
    '/partner/schedule/weekly',
    { method: 'PUT', body: schedule },
  );
  if (body && 'schedule' in body) return body.schedule;
  return body as WeeklySchedule;
}

export async function getBlackoutDates(): Promise<BlackoutDate[]> {
  const body = await apiRequest<{ dates?: BlackoutDate[] } | BlackoutDate[]>(
    '/partner/blackout-dates',
  );
  if (Array.isArray(body)) return body;
  return body.dates ?? [];
}

export async function addBlackoutDate(item: BlackoutDate): Promise<BlackoutDate> {
  const body = await apiRequest<{ date: BlackoutDate } | BlackoutDate>('/partner/blackout-dates', {
    method: 'POST',
    body: item,
  });
  const candidate = (body as { date?: unknown } | null)?.date;
  if (candidate && typeof candidate === 'object') {
    return candidate as BlackoutDate;
  }
  return body as BlackoutDate;
}

export async function removeBlackoutDate(date: string): Promise<void> {
  await apiRequest<void>(`/partner/blackout-dates/${encodeURIComponent(date)}`, { method: 'DELETE' });
}

/* ────────────────────────────  ГРУППА  ────────────────────────────── */

export async function getPartnerBookings(date: string): Promise<PartnerBookingsView> {
  const body = await apiRequest<PartnerBookingsView>('/partner/bookings', { query: { date } });
  return { date: body.date ?? date, groups: body.groups ?? [] };
}

/* ──────────────────────────  ОТЧЁТНОСТЬ  ──────────────────────────── */

export async function getPartnerReport(from: string, to: string): Promise<PartnerReportView> {
  return apiRequest<PartnerReportView>('/partner/report', { query: { from, to } });
}

/* ───────────────────────────  ПРОГРАММЫ  ──────────────────────────── */

export async function getPartnerExcursions(): Promise<Excursion[]> {
  const body = await apiRequest<{ excursions?: Excursion[] } | Excursion[]>('/partner/excursions');
  if (Array.isArray(body)) return body;
  return body.excursions ?? [];
}

export async function getPartnerSites(): Promise<Site[]> {
  const body = await apiRequest<{ sites?: Site[] } | Site[]>('/partner/sites');
  if (Array.isArray(body)) return body;
  return body.sites ?? [];
}

export async function createPartnerExcursion(input: ExcursionInput): Promise<Excursion> {
  const body = await apiRequest<{ excursion: Excursion } | Excursion>('/partner/excursions', {
    method: 'POST',
    body: input,
  });
  if (body && 'excursion' in body) return body.excursion;
  return body as Excursion;
}

export async function updatePartnerExcursion(
  id: string,
  patch: Partial<ExcursionInput>,
): Promise<Excursion> {
  const body = await apiRequest<{ excursion: Excursion } | Excursion>(
    `/partner/excursions/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
  if (body && 'excursion' in body) return body.excursion;
  return body as Excursion;
}

export async function archivePartnerExcursion(id: string): Promise<Excursion> {
  const body = await apiRequest<{ excursion: Excursion } | Excursion>(
    `/partner/excursions/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
  );
  if (body && 'excursion' in body) return body.excursion;
  return body as Excursion;
}

/* ───────────────────  B2B-ЗАЯВКИ И ДЕЛОВЫЕ ВИЗИТЫ  ─────────────────── */

export async function getB2BRequests(status?: B2BRequestStatus): Promise<B2BRequestPage> {
  const body = await apiRequest<{ items?: B2BRequest[]; nextCursor?: string | null }>(
    '/partner/b2b-requests',
    { query: { status } },
  );
  return {
    items: body.items ?? [],
    nextCursor: body.nextCursor ?? null,
  };
}

export async function updateB2BRequestStatus(
  id: string,
  status: B2BRequestStatus,
): Promise<B2BRequest> {
  const body = await apiRequest<{ request: B2BRequest } | B2BRequest>(
    `/partner/b2b-requests/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: { status } },
  );
  if (body && 'request' in body) return body.request;
  return body as B2BRequest;
}

export async function getCoordinator(): Promise<CoordinatorContact> {
  const body = await apiRequest<{ coordinator: CoordinatorContact } | CoordinatorContact>(
    '/partner/coordinator',
  );
  if (body && 'coordinator' in body) return body.coordinator;
  return body as CoordinatorContact;
}

/* ───────────────────  ПРОПУСКА И ЧЕК-ИНЫ НА ПРОХОДНОЙ  ─────────────── */

/**
 * Проверка пропуска сервером (FR-MGR-01). Ответ нормализуется:
 * сервер может отдавать поля как в snake_case, так и в camelCase.
 */
export async function verifyPassAtGate(code: string): Promise<GatePassInfo> {
  const body = await apiRequest<Record<string, unknown>>(
    `/passes/${encodeURIComponent(code)}/verify`,
  );
  const source = body ?? {};
  const visitor = source['visitor'] as string | undefined;
  const guestCount = source['guestCount'] ?? source['guest_count'];
  const slotStartsAt = (source['slotStartsAt'] ?? source['slot_starts_at']) as string | undefined;
  const slotEndsAt = (source['slotEndsAt'] ?? source['slot_ends_at']) as string | undefined;
  const safetyPassedRaw = source['safetyPassed'] ?? source['safety_passed'];

  return {
    ok: Boolean(source['ok']),
    reason: typeof source['reason'] === 'string' ? source['reason'] : undefined,
    visitor,
    bookingId: typeof source['bookingId'] === 'string' ? source['bookingId'] : undefined,
    guestCount: typeof guestCount === 'number' ? guestCount : undefined,
    slotStartsAt,
    slotEndsAt,
    safetyPassed: typeof safetyPassedRaw === 'boolean' ? safetyPassedRaw : null,
    offline: Boolean(source['offline']),
  };
}

export async function createCheckin(request: CreateCheckinRequest): Promise<CreateCheckinResponse> {
  return apiRequest<CreateCheckinResponse>('/checkins', { method: 'POST', body: request });
}

/** Тип ответа сервера на проверку пропуска — используется в тестах и моках. */
export type GateVerifyRaw = VerifyPassResponse;

/**
 * Ключ офлайн-проверки: производный ключ своего объекта (ADR-002).
 * Мастер-ключ подписи сервер не выдаёт.
 */
export async function getOfflineVerificationKey(): Promise<OfflineVerificationKey> {
  const body = await apiRequest<Record<string, unknown>>('/partner/offline/verification-key');
  const source = body ?? {};
  const keyBase64 = (source['keyBase64'] ?? source['key_base64']) as string | undefined;
  const enterpriseId = (source['enterpriseId'] ?? source['enterprise_id']) as string | undefined;
  const kid = (source['kid'] ?? source['keyId'] ?? source['key_id']) as string | undefined;
  if (!keyBase64 || !enterpriseId) {
    throw new Error('Сервер не вернул ключ офлайн-проверки');
  }
  return {
    enterpriseId,
    keyBase64,
    kid: kid ?? '',
    issuedAt: (source['issuedAt'] ?? source['issued_at']) as string ?? new Date().toISOString(),
    expiresAt: (source['expiresAt'] ?? source['expires_at']) as string | null ?? null,
  };
}
